import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, History, Loader2, Send, Truck, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/pages/Leads";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

/** Everything you can do to an e-way bill that already exists: Part-B, cancel, request, history. */
export function EwayManagePanel({ challan, precheck, onDone, onClose }) {
  const ex = precheck.existing || {};
  const [mode, setMode] = useState("");
  const [reasons, setReasons] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const [partB, setPartB] = useState({
    vehicle_number: "", transport_mode: String(ex.transport_mode || "1"),
    reason_code: "1", reason: "", transport_doc_no: "", transport_doc_date: "",
  });
  const [cancelForm, setCancelForm] = useState({ reason_code: "2", remarks: "" });
  const [reqForm, setReqForm] = useState({ action: "part_b", reason: "", vehicle_number: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/eway/reasons");
      setReasons(data);
    } catch (e) { /* reasons are optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "high_impact") {
        setAck("pending");
        toast.warning(detail.message);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const doPartB = () => run(async () => {
    await api.post(`/eway/challans/${challan.id}/part-b`, partB);
    toast.success("Part-B updated — old vehicle kept in history");
    setMode("");
  });

  const doCancel = () => run(async () => {
    await api.post(`/eway/challans/${challan.id}/cancel`,
      { ...cancelForm, acknowledge_dispatched: ack === true });
    toast.success("E-way bill cancelled officially. Challan and stock were not changed.");
    onClose?.();
  });

  const doRequest = () => run(async () => {
    await api.post(`/eway/challans/${challan.id}/request`, reqForm);
    toast.success("Sent to the Owner's Approval Center");
    setMode("");
  });

  const cancelBlocked = ex.within_cancel_window === false;

  return (
    <div className="space-y-4" data-testid="eway-manage-panel">
      <div className="surface space-y-1 p-3">
        <div className="text-xs text-muted-foreground">Official e-way bill</div>
        <div className="font-mono-num text-2xl" data-testid="eway-manage-number">
          {ex.eway_number || "—"}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={ex.eway_status === "Cancelled" ? "destructive" : "default"}
            className="text-[10px]" data-testid="eway-manage-status">
            {ex.eway_status || "Generated"}
          </Badge>
          {ex.source && (
            <Badge variant="outline" className="text-[10px]">{ex.source}</Badge>
          )}
          {ex.mock && (
            <Badge variant="destructive" className="text-[10px]" data-testid="eway-manage-mock">
              SANDBOX — not official
            </Badge>
          )}
        </div>
        <div className="grid gap-1 pt-2 text-xs sm:grid-cols-2">
          {[["Generated on", ex.eway_date || "—"], ["Valid till", ex.valid_till || "—"],
            ["Vehicle", challan.vehicle_number || "—"],
            ["Dispatch status", challan.status || "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
          ))}
        </div>
      </div>

      {ex.pending_request && (
        <div className="surface p-3 text-xs" data-testid="eway-pending-request">
          A {ex.pending_request.action === "eway_cancel" ? "cancellation" : "Part-B"} request from{" "}
          {ex.pending_request.requested_by} is waiting for the Owner's decision. Nothing on this
          bill changes until then.
        </div>
      )}

      {!ex.pending_request && ex.eway_status !== "Cancelled" && (
        <div className="flex flex-wrap gap-2">
          {precheck.can_part_b ? (
            <Button size="sm" variant="outline" className="gap-1"
              data-testid="eway-part-b-open" onClick={() => setMode("part_b")}>
              <Truck className="h-3.5 w-3.5" /> Update vehicle / Part-B
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="gap-1"
              data-testid="eway-request-edit-open"
              onClick={() => { setReqForm({ ...reqForm, action: "part_b" }); setMode("request"); }}>
              <Send className="h-3.5 w-3.5" /> Request edit
            </Button>
          )}
          {precheck.can_cancel ? (
            <Button size="sm" variant="destructive" className="gap-1" disabled={cancelBlocked}
              data-testid="eway-cancel-open" onClick={() => setMode("cancel")}>
              <XCircle className="h-3.5 w-3.5" /> Cancel e-way bill
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="gap-1"
              data-testid="eway-request-cancel-open"
              onClick={() => { setReqForm({ ...reqForm, action: "cancel" }); setMode("request"); }}>
              <Send className="h-3.5 w-3.5" /> Request cancellation
            </Button>
          )}
        </div>
      )}

      {cancelBlocked && precheck.can_cancel && (
        <p className="text-xs text-amber-600" data-testid="eway-cancel-window-note">
          GST rule: an e-way bill can only be cancelled within {ex.cancel_window_hours} hours of
          generation. This one is older, so cancellation is closed — raise a credit note or a fresh
          bill instead.
        </p>
      )}

      {mode === "part_b" && (
        <div className="surface space-y-3 p-3" data-testid="eway-part-b-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="New vehicle number *">
              <Input data-testid="part-b-vehicle" value={partB.vehicle_number}
                onChange={(e) => setPartB({ ...partB, vehicle_number: e.target.value })} />
            </Field>
            <Field label="Transport mode">
              <Select value={partB.transport_mode}
                onValueChange={(v) => setPartB({ ...partB, transport_mode: v })}>
                <SelectTrigger data-testid="part-b-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(reasons?.transport_modes || [{ value: "1", label: "Road" }]).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Reason for change">
              <Select value={partB.reason_code}
                onValueChange={(v) => setPartB({ ...partB, reason_code: v })}>
                <SelectTrigger data-testid="part-b-reason-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(reasons?.part_b || [{ value: "1", label: "Due to break down" }]).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Transport document number">
              <Input data-testid="part-b-doc-no" value={partB.transport_doc_no}
                onChange={(e) => setPartB({ ...partB, transport_doc_no: e.target.value })} />
            </Field>
          </div>
          <Field label="Remarks">
            <Input data-testid="part-b-remarks" value={partB.reason}
              onChange={(e) => setPartB({ ...partB, reason: e.target.value })} />
          </Field>
          <Button size="sm" disabled={busy} onClick={doPartB} data-testid="part-b-submit">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Update Part-B
          </Button>
        </div>
      )}

      {mode === "cancel" && (
        <div className="surface space-y-3 p-3" data-testid="eway-cancel-form">
          <p className="text-xs text-muted-foreground">
            The delivery challan will stay as it is and no stock will be reversed — only the e-way
            bill is cancelled with the government.
          </p>
          <Field label="Reason">
            <Select value={cancelForm.reason_code}
              onValueChange={(v) => setCancelForm({ ...cancelForm, reason_code: v })}>
              <SelectTrigger data-testid="cancel-reason-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(reasons?.cancel || [{ value: "2", label: "Order cancelled" }]).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Remarks (max 50 characters)">
            <Input maxLength={50} data-testid="cancel-remarks" value={cancelForm.remarks}
              onChange={(e) => setCancelForm({ ...cancelForm, remarks: e.target.value })} />
          </Field>
          {(ex.dispatch_confirmed || ack === "pending") && (
            <label className="flex items-start gap-2 text-xs text-amber-600"
              data-testid="cancel-high-impact-warning">
              <input type="checkbox" data-testid="cancel-ack-checkbox" checked={ack === true}
                onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
              <span>
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                This consignment is already dispatched. I understand the challan and stock history
                will not change.
              </span>
            </label>
          )}
          <Button size="sm" variant="destructive" disabled={busy} onClick={doCancel}
            data-testid="cancel-submit">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Cancel officially
          </Button>
        </div>
      )}

      {mode === "request" && (
        <div className="surface space-y-3 p-3" data-testid="eway-request-form">
          <p className="text-xs text-muted-foreground">
            Staff cannot change an official e-way bill directly. Your request goes to the Owner's
            Approval Center with the full before → after detail.
          </p>
          {reqForm.action === "part_b" && (
            <Field label="Vehicle number you want">
              <Input data-testid="request-vehicle" value={reqForm.vehicle_number}
                onChange={(e) => setReqForm({ ...reqForm, vehicle_number: e.target.value })} />
            </Field>
          )}
          <Field label="Why is this needed? *">
            <Textarea rows={2} data-testid="request-reason" value={reqForm.reason}
              onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} />
          </Field>
          <Button size="sm" disabled={busy || reqForm.reason.trim().length < 5}
            onClick={doRequest} data-testid="request-submit">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Send {reqForm.action === "cancel" ? "cancellation" : "edit"} request
          </Button>
        </div>
      )}

      {(ex.part_b_history?.length > 0 || ex.cancel_history?.length > 0) && (
        <div className="space-y-1.5" data-testid="eway-history-list">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <History className="h-3.5 w-3.5" /> History
          </div>
          {(ex.part_b_history || []).map((h, i) => (
            <div key={`pb-${i}`} className="text-[11px] text-muted-foreground"
              data-testid={`eway-history-partb-${i}`}>
              Part-B: {h.old_vehicle || "—"} → <b>{h.new_vehicle}</b> • {h.reason} • {h.by} •{" "}
              {String(h.at).slice(0, 16).replace("T", " ")}
              {h.origin?.startsWith("approved") ? " • owner approved" : ""}
            </div>
          ))}
          {(ex.cancel_history || []).map((h, i) => (
            <div key={`cx-${i}`} className="text-[11px] text-destructive"
              data-testid={`eway-history-cancel-${i}`}>
              Cancelled: {h.remarks} • {h.by} • {String(h.at).slice(0, 16).replace("T", " ")}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
